import { FC, useEffect, useMemo, useRef } from "react";
import { supabase } from "supabase/client";
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import cloneDeep from "lodash.clonedeep";

import useNotifier from "hooks/useNotifier";
import {
  addArrayItem,
  addArrayItems,
  removeArrayItem,
  updateArrayItem,
  updateArrayItems,
} from "utils/arrayOfObjectsOperations";
import IndexDB from "services/IndexDB";
import { GetAllGroups, GroupRow } from "api/group";
import EventApi, { EventRow } from "api/event";
import FriendApi, { FriendData, FriendRequest, FriendRow } from "api/friend";
import ProfileApi from "api/profile";
import StorageApi, { StorageBuckets } from "api/storage";
import VoteApi, { VoteRow, VoteByEventId } from "api/vote";
import useInterval from "hooks/useInterval";
import useBoundStore from "store";
import { AugmentedEvent, AugmentedGroup } from "store/appDataSlice";
import { appStatus } from "tests/mocks/store";
import { useShallow } from "zustand/shallow";

enum Table {
  friends = "friends",
  groups = "groups",
  events = "events",
  votes = "votes",
}

interface RealtimeListenerProps {
  children: React.ReactNode;
}

const RealtimeListener: FC<RealtimeListenerProps> = (props: RealtimeListenerProps) => {
  const { children } = props;
  const userAuth = useBoundStore((state) => state.user.auth);
  const { isMobileDevice, setMiscellaneous } = useBoundStore(
    useShallow((state) => ({
      appStatus: state.miscellaneous.appStatus,
      isMobileDevice: state.miscellaneous.isMobileDevice,
      setMiscellaneous: state.miscellaneous.set,
    }))
  );
  const { events, friends, friendRequests, groups, groupsUsers, votes, setAppData } = useBoundStore(
    useShallow((state) => ({
      events: state.appData.events,
      friends: state.appData.friends,
      friendRequests: state.appData.friendRequests,
      groups: state.appData.groups,
      groupsUsers: state.appData.groupsUsers,
      votes: state.appData.votes,
      setAppData: state.appData.set,
    }))
  );
  const isAppFocused = useBoundStore((state) => state.miscellaneous.isAppFocused);
  const setSmallAvatars = useBoundStore((state) => state.miscellaneous.set);
  const isListening = useRef<boolean>(false);
  const groupPayloadsRef = useRef<RealtimePostgresChangesPayload<GroupRow>[]>([]);
  const eventPayloadsRef = useRef<RealtimePostgresChangesPayload<EventRow>[]>([]);
  const friendPayloadsRef = useRef<RealtimePostgresChangesPayload<FriendRow>[]>([]);
  const votePayloadsRef = useRef<RealtimePostgresChangesPayload<VoteRow>[]>([]);
  const { showNotification } = useNotifier();
  const eventApi = useMemo(() => new EventApi(showNotification), [showNotification]);
  const friendApi = useMemo(() => new FriendApi(showNotification), [showNotification]);
  const profileApi = useMemo(() => new ProfileApi(showNotification), [showNotification]);
  const voteApi = useMemo(() => new VoteApi(showNotification), [showNotification]);
  const storageApi = useMemo(() => new StorageApi(), []);

  /**
   * Get and set group users
   */
  async function fetchGroupUsers(updatedGroup: GetAllGroups): Promise<void> {
    // Remove users for which we have information about
    const groupsUsersIds = groupsUsers.map((user) => user.uid);
    const usersToBeFetched = updatedGroup.users?.filter((userId) => !groupsUsersIds.includes(userId));

    if (usersToBeFetched) {
      const { users } = await profileApi.getUsersByIds(usersToBeFetched);
      setAppData({
        groupsUsers: [...groupsUsers, ...users],
      });
    }
  }

  /**
   * Update store groups
   */
  async function updateGroups(): Promise<void> {
    if (groupPayloadsRef.current.length) {
      const groupPayloads = cloneDeep(groupPayloadsRef.current);
      groupPayloadsRef.current = [];

      let updatedEvents: AugmentedEvent[] = events;
      let updatedGroups: AugmentedGroup[] = groups;
      let updatedVotes: VoteByEventId[] = votes;
      for (let i = 0; i < groupPayloads.length; i += 1) {
        const groupPayload = groupPayloads[i];
        let newGroup: AugmentedGroup | undefined;
        const indexDB = await IndexDB.getInstance();

        if (groupPayload.eventType === "INSERT" || groupPayload.eventType === "UPDATE") {
          newGroup = {
            id: groupPayload.new.id,
            name: groupPayload.new.name,
            avatar: groupPayload.new.avatar,
            users: groupPayload.new.users,
            admins: groupPayload.new.admins,
            roles: (groupPayload.new as any).roles ?? {},
            stale: false,
          };
        }

        const saveAvatarToIndexDB = async () => {
          if (newGroup?.avatar) {
            // Get avatar file
            const { file } = await storageApi.getFile(newGroup.avatar, StorageBuckets.GROUPS_AVATARS);
            // Save new avatar file to indexDB
            if (file) {
              await indexDB.addSmallAvatar(file, newGroup.avatar);

              // Add avatar file to state
              setSmallAvatars({ smallAvatars: { [newGroup.avatar]: file } });
            }
          }
        };

        switch (groupPayload.eventType) {
          case "INSERT":
            updatedGroups = addArrayItem(newGroup!, updatedGroups);
            // Update groupsUsers array
            fetchGroupUsers(newGroup!);
            break;
          case "UPDATE":
            if (newGroup!.users?.includes(userAuth!.id)) {
              // If user was added to a newGroup which already existed add that newGroup
              // to his groups
              if (!groups.find((group) => group.id === newGroup!.id)) {
                saveAvatarToIndexDB();

                // Fetch the events which belongs to that group
                const { events: fetchedEvents } = await eventApi.getEventsByGroup(newGroup!.id);
                const augmentedEvents: AugmentedEvent[] = fetchedEvents.map((event) => ({
                  ...event,
                  stale: false,
                }));
                updatedEvents = addArrayItems(augmentedEvents, updatedEvents);

                // Fetch votes for the fetched events
                const eventIds = events.map((event) => event.id);
                const { votes: fetchedVotes } = await voteApi.getVotesByEventsIds(eventIds);
                updatedVotes = updateArrayItems(fetchedVotes, updatedVotes);

                updatedGroups = addArrayItem(newGroup!, updatedGroups);
              } else {
                // It means the user is already in the group and that the group was updated
                updatedGroups = updateArrayItem(newGroup!, updatedGroups);

                saveAvatarToIndexDB();

                // Remove old avatar
                const oldGroupAvatar = groups.find((group) => group.id === newGroup!.id)?.avatar;
                if (oldGroupAvatar) {
                  await indexDB.deleteSmallAvatar(oldGroupAvatar);
                }
              }

              // Update groupsUsers array
              fetchGroupUsers(newGroup!);
            } else {
              // Else it means that the user left/was removed from the group
              // so the group can be deleted from his groups
              updatedGroups = removeArrayItem(newGroup!.id, updatedGroups);
            }

            break;
          case "DELETE": {
            // Remove old avatar
            const oldGroupAvatar = groups.find((group) => group.id === groupPayload.old.id)?.avatar;
            if (oldGroupAvatar) {
              await indexDB.deleteSmallAvatar(oldGroupAvatar);
            }
            if (groupPayload.old.id) {
              updatedGroups = removeArrayItem(groupPayload.old.id, updatedGroups);
            }
            break;
          }
          default:
            break;
        }
      }

      setAppData({
        events: updatedEvents,
        groups: updatedGroups,
        votes: updatedVotes,
      });
    }
  }

  /**
   * Update store events
   */
  function updateEvents(): void {
    if (eventPayloadsRef.current.length) {
      const eventPayloads = cloneDeep(eventPayloadsRef.current);
      eventPayloadsRef.current = [];

      let updatedEvents: AugmentedEvent[] = events;
      for (let i = 0; i < eventPayloads.length; i += 1) {
        const eventPayload = eventPayloads[i];
        const event = { ...(eventPayload.new as EventRow), stale: false };

        switch (eventPayload.eventType) {
          case "INSERT":
            updatedEvents = addArrayItem(event, updatedEvents);

            break;
          case "UPDATE":
            // Remove event from events state if the status is closed
            if (eventPayload.new.status === "closed") {
              updatedEvents = removeArrayItem(eventPayload.new.id, updatedEvents);
            } else {
              updatedEvents = updateArrayItem(event, updatedEvents);
            }
            break;
          case "DELETE":
            if (eventPayload.old.id) {
              updatedEvents = removeArrayItem(eventPayload.old.id, updatedEvents);
            }
            break;
          default:
            break;
        }
      }

      setAppData({ events: updatedEvents });
    }
  }

  async function updateFriends(): Promise<void> {
    if (friendPayloadsRef.current.length) {
      const friendPayloads = cloneDeep(friendPayloadsRef.current);
      friendPayloadsRef.current = [];

      let updatedFriends: FriendData[] = friends;
      let updatedFriendRequests: FriendRequest[] = friendRequests;
      for (let i = 0; i < friendPayloads.length; i += 1) {
        const friendPayload = friendPayloads[i];

        const indexDB = await IndexDB.getInstance();
        const authUid = userAuth!.id;

        switch (friendPayload.eventType) {
          // It means a new friend request was sent/received
          case "INSERT": {
            const { friendRequest } = await friendApi.getFriendRequest(friendPayload.new.id, authUid);

            if (friendRequest) {
              updatedFriendRequests = addArrayItem(friendRequest, updatedFriendRequests);
            }

            break;
          }
          // It means a friend request was accepted
          case "UPDATE": {
            const { friend } = await friendApi.getFriend(friendPayload.new.id, authUid);

            if (friend) {
              updatedFriends = addArrayItem(friend, updatedFriends);
              updatedFriendRequests = removeArrayItem(friendPayload.new.id, updatedFriendRequests);

              // Add user avatar to indexDB
              if (friend.avatar) {
                const { file } = await storageApi.getFile(friend.avatar, StorageBuckets.PROFILES_AVATARS);
                if (file) {
                  indexDB.addSmallAvatar(file, friend.avatar);
                  // Add avatar file to state
                  setSmallAvatars({ smallAvatars: { [friend.avatar]: file } });
                }
              }
            }
            break;
          }
          // It means a friend or a friend requests was deleted
          case "DELETE": {
            const oldFriend = friends.find((friend) => friend.id === friendPayload.old.id);

            // This means the payload refers to a friend
            if (oldFriend) {
              updatedFriends = removeArrayItem(oldFriend.id, updatedFriends);

              if (oldFriend.avatar) {
                indexDB.deleteSmallAvatar(oldFriend.avatar);
              }
            } else if (friendPayload.old.id) {
              // Else the payload refers to a friend request
              updatedFriendRequests = removeArrayItem(friendPayload.old.id, updatedFriendRequests);
            }

            break;
          }
          default:
            break;
        }
      }

      setAppData({
        friends: updatedFriends,
        friendRequests: updatedFriendRequests,
      });
    }
  }

  /**
   * Update store votes
   */
  function updateVotes(): void {
    if (votePayloadsRef.current.length) {
      const votePayloads = cloneDeep(votePayloadsRef.current);
      votePayloadsRef.current = [];

      let updatedVotes: VoteByEventId[] = votes;
      for (let i = 0; i < votePayloads.length; i += 1) {
        const votePayload = votePayloads[i];
        const newEvent = votePayload.new as VoteRow;
        const vote: VoteByEventId = {
          event_id: newEvent.event_id,
          id: newEvent.id,
          type: newEvent.type,
          uid: newEvent.uid,
          joined_on: newEvent.joined_on,
          vehicle_details: newEvent.vehicle_details,
          passengers: newEvent.passengers,
          updated_at: newEvent.updated_at,
        };

        switch (votePayload.eventType) {
          case "INSERT":
            updatedVotes = addArrayItem(vote, updatedVotes);
            break;
          case "UPDATE":
            updatedVotes = updateArrayItem(vote, updatedVotes);
            break;
          case "DELETE":
            if (votePayload.old.id) {
              updatedVotes = removeArrayItem(votePayload.old.id, updatedVotes);
            }
            break;
          default:
            break;
        }
      }

      setAppData({
        votes: updatedVotes,
      });
    }
  }

  /**
   * Handle realtime subscription payload
   * @param {Object} payload
   */
  function handlePayload(
    payload: RealtimePostgresChangesPayload<{
      [key: string]: any;
    }>
  ): void {
    switch (payload.table as Table) {
      case "groups":
        groupPayloadsRef.current.push(payload as RealtimePostgresChangesPayload<GroupRow>);
        break;
      case "events":
        eventPayloadsRef.current.push(payload as RealtimePostgresChangesPayload<EventRow>);
        break;
      case "friends":
        friendPayloadsRef.current.push(payload as RealtimePostgresChangesPayload<FriendRow>);
        break;
      case "votes":
        votePayloadsRef.current.push(payload as RealtimePostgresChangesPayload<VoteRow>);
        break;
      default:
        break;
    }
  }

  /**
   * Interval was used to update the state for all the date because doing updates for
   * every realtime update would result in a lot of rerenders and missing data since
   * react batches the state updates
   */
  useInterval(() => {
    updateVotes();
    updateFriends();
    updateEvents();
    updateGroups();
  }, 500);

  /**
   * Subscribe to realtime
   */
  useEffect(() => {
    if (userAuth?.id && !isListening.current && isAppFocused && appStatus) {
      supabase
        .channel("*")
        .on("postgres_changes", { event: "*", schema: "*" }, handlePayload)
        .subscribe((status) => {
          setMiscellaneous({ realtimeSubscribed: status === "SUBSCRIBED" });
        });
      isListening.current = true;
    }

    if (!isAppFocused && isListening.current && isMobileDevice) {
      supabase.removeAllChannels();
      isListening.current = false;
    }
  }, [userAuth?.id, isAppFocused, isListening, isMobileDevice]);

  return children;
};

export default RealtimeListener;
